import React, { useContext, useEffect, useState } from 'react';

import {
  type GuidedOnboardingPayload,
  type IGroupDefinition,
} from 'loot-core/shared/onboarding';

import { OnboardingContext } from '../../../../context/onboarding/OnboardingContext';
import { AsyncStorageKey, useStorage } from '../../../../hooks/useStorage';
import { SvgClose } from '../../../../icons/v1';
import { theme } from '../../../../style';
import { Button } from '../../../common/Button';
import { Input } from '../../../common/Input';
import { View } from '../../../common/View';
import { FormField } from '../../../forms';

type OnboardingMultipleInputProps = {
  groupDefinition: IGroupDefinition;
};

export function OnboardingMultipleInput({
  groupDefinition,
}: OnboardingMultipleInputProps) {
  const { collectData } = useContext(OnboardingContext);
  const { getItem: getOnboardingData } = useStorage(AsyncStorageKey.ONBOARDING_DATA);
  const generateDefaultInput = () => ({
    index: new Date().getTime(),
    name: '',
  });
  const [multipleInputs, setMultipleInputs] = useState([
    generateDefaultInput(),
  ]);

  useEffect(() => {
    (async () => {
      const onboardingData: GuidedOnboardingPayload = await getOnboardingData();

      if (onboardingData) {
        const group = onboardingData.groups?.find(
          g => g.name === groupDefinition.name,
        );

        if (group && group.categories?.length) {
          setMultipleInputs(
            group.categories.map((cat, index) => ({
              index,
              name: cat,
            })),
          );
        }
      }
    })();
  }, []);

  return (
    <View style={{ marginBottom: 20, gap: 10 }}>
      {multipleInputs.map((income, index) => (
        <View
          key={income.index}
          style={{
            gap: 5,
            flexDirection: 'row',
            position: 'relative',
          }}
        >
          <FormField style={{ flex: 1 }}>
            <Input
              type="text"
              name="name"
              value={income.name}
              required={true}
              onChange={async e => {
                await updateValue(index, 'name', e.target.value);
              }}
            />
          </FormField>

          {index > 0 ? (
            <Button
              style={{
                width: 30,
                backgroundColor: theme.tableBorder,
              }}
              type="bare"
              isSubmit={false}
              onClick={e => {
                const _multipleInputs = [...multipleInputs];
                _multipleInputs.splice(index, 1);
                setMultipleInputs([..._multipleInputs]);
              }}
            >
              <SvgClose width={10} />
            </Button>
          ) : null}
        </View>
      ))}
      <Button
        style={{ width: 150, alignSelf: 'flex-end' }}
        type="normal"
        isSubmit={false}
        onClick={e => {
          setMultipleInputs([...multipleInputs, generateDefaultInput()]);
        }}
      >
        Add another income
      </Button>
    </View>
  );

  async function updateValue(index, name, value) {
    const _multipleInputs = [...multipleInputs];
    _multipleInputs[index][name] = value;
    setMultipleInputs(_multipleInputs);

    await collectData?.(
      groupDefinition.name,
      _multipleInputs.map(inp => inp.name),
    );
  }
}
